import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Progress,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { type ReactNode, useState } from 'react';
import { AmendInvoiceModal } from '@/components/InvoiceDetailsModal/AmendInvoiceModal';
import { CancelInvoiceModal } from '@/components/InvoicesPage/CancelInvoiceModal';
import {
  PaymentConfirmationData,
  PaymentConfirmationForm,
} from '@/components/PaymentConfirmationForm';
import { useUndoCountdown } from '@/hooks/useUndoCountdown';
import { ApiError } from '@/lib/api';
import {
  type InvoiceDetails,
  type PaymentMovement,
  refetchInvoiceDetails,
  useAddPaymentReport,
  useCreateOpportunity,
  useDownloadInvoicePDF,
  useRecordMovement,
} from '@/models/invoices';
import { TInvoice } from '@/types';
import { PaymentHistory } from './PaymentHistory';
import { SettleIssueModal } from './SettleIssueModal';

const UNDO_SECONDS = 10;

export type InvoiceActionHandlers = {
  onDownloadPDF: (invoice: TInvoice) => void;
  onAddPaymentReport: (invoice: TInvoice) => void;
  onCancelInvoice: (invoice: TInvoice) => void;
  onRecordMovement: (invoice: TInvoice) => void;
  onSettle: (invoice: TInvoice) => void;
  onMondayOpportunityCreate: (invoice: TInvoice) => void;
  onAmend: (invoice: TInvoice) => void;
};

type Snapshot = {
  paidAmount: number;
  paymentSeq: number;
  payments: PaymentMovement[];
};

const snapshotOf = (invoice: InvoiceDetails): Snapshot => ({
  paidAmount: invoice.paidAmount ?? 0,
  paymentSeq: invoice.paymentSeq ?? 0,
  payments: invoice.payments ?? [],
});

// The accountant's local calendar day - toISOString() would flip to the UTC date, which
// around midnight is a different day than the one on their screen.
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
};

const fmt = (n: number, currency: string) =>
  `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

/**
 * What the proforma asks for, what is collected on it and what remains - and, once an amount is
 * entered, what they will be after this entry - so the figure is entered against the due amount.
 */
function DueSummary({
  due,
  collected,
  after,
  currency,
}: {
  due: number;
  collected: number;
  after: number | null;
  currency: string;
}) {
  const remaining = (c: number) => Math.max(due - c, 0);
  return (
    <Stack
      gap={2}
      p="xs"
      bg="var(--mantine-color-gray-light)"
      style={{ borderRadius: 6 }}
    >
      <Group justify="space-between" gap="xs">
        <Text size="sm">
          Due <b>{fmt(due, currency)}</b>
        </Text>
        <Text size="sm" c="dimmed">
          collected {fmt(collected, currency)} · remaining{' '}
          {fmt(remaining(collected), currency)}
        </Text>
      </Group>
      {after != null && (
        <Text size="xs" c={after > due ? 'orange' : 'dimmed'}>
          After this entry: collected {fmt(after, currency)} ·{' '}
          {after > due
            ? `${fmt(after - due, currency)} more than due`
            : `remaining ${fmt(remaining(after), currency)}`}
        </Text>
      )}
    </Stack>
  );
}

// Bundles the invoice mutations + their sub-modals so the details surface (drawer or
// standalone page) can offer full actions.
export function useInvoiceActions(): {
  handlers: InvoiceActionHandlers;
  modals: ReactNode;
} {
  const downloadPDFMutation = useDownloadInvoicePDF();
  const recordMovement = useRecordMovement();
  const addReport = useAddPaymentReport();
  const createOpportunity = useCreateOpportunity();

  const [target, setTarget] = useState<TInvoice | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const [movementOpen, setMovementOpen] = useState(false);
  const [direction, setDirection] = useState<'collection' | 'deduction'>(
    'collection',
  );
  const [amountValue, setAmountValue] = useState<number | string>('');
  const [collectedDate, setCollectedDate] = useState<string | null>(null);
  const [senderName, setSenderName] = useState('');
  const [linkedReport, setLinkedReport] = useState<string | null>(null);
  const [reportKey, setReportKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notice, setNotice] = useState<{
    title: string;
    message: string;
    tone: 'red' | 'orange';
  } | null>(null);

  const [settleOpen, setSettleOpen] = useState(false);

  // Cached list data is enough to open a modal, never enough to write against: the movement
  // count decides whether an entry is accepted, and a stale one is refused.
  const openWithFreshInvoice = async (invoice: TInvoice, open: () => void) => {
    setTarget(invoice);
    setSnapshot(null);
    setNotice(null);
    open();
    try {
      const fresh = await refetchInvoiceDetails(invoice.id.toString());
      setTarget(fresh);
      setSnapshot(snapshotOf(fresh));
    } catch {
      setNotice({
        title: 'Could not load this invoice',
        message:
          'Nothing can be recorded until its current figures are known. Close this and try again.',
        tone: 'orange',
      });
    }
  };

  const handlers: InvoiceActionHandlers = {
    onDownloadPDF: (invoice) =>
      downloadPDFMutation.mutate(invoice.id.toString()),
    onAddPaymentReport: (invoice) => {
      setTarget(invoice);
      setReportKey(crypto.randomUUID());
      setPaymentOpen(true);
    },
    onAmend: (invoice) => {
      setTarget(invoice);
      setAmendOpen(true);
    },
    onCancelInvoice: (invoice) =>
      void openWithFreshInvoice(invoice, () => setCancelOpen(true)),
    onRecordMovement: (invoice) => {
      setDirection('collection');
      setAmountValue('');
      setCollectedDate(today());
      setSenderName('');
      setLinkedReport(null);
      void openWithFreshInvoice(invoice, () => setMovementOpen(true));
    },
    onSettle: (invoice) => {
      setTarget(invoice);
      setSettleOpen(true);
    },
    onMondayOpportunityCreate: (invoice) =>
      createOpportunity.mutate(invoice.id),
  };

  const activeReports = (
    (target as InvoiceDetails | null)?.paymentReports ?? []
  ).filter((r) => r.removedAt == null);

  const parsed =
    typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue;
  // A NumberInput holding something unparseable yields NaN, and `NaN <= 0` is false - so
  // guarding on that alone leaves the button live on a figure the submit drops on the floor.
  const amountIsMoney = Number.isFinite(parsed) && parsed > 0;
  const signed = direction === 'deduction' ? -(parsed || 0) : parsed || 0;
  const resulting = (snapshot?.paidAmount ?? 0) + signed;

  const submitMovement = async () => {
    if (!target || !snapshot || !collectedDate || !amountIsMoney) return;
    setNotice(null);
    setSubmitting(true);
    try {
      const res = await recordMovement.mutateAsync({
        id: target.id.toString(),
        amount: signed,
        collectedAt: collectedDate,
        expectedPaymentSeq: snapshot.paymentSeq,
        senderName: signed > 0 ? senderName.trim() || null : null,
        paymentReportId:
          signed > 0 && linkedReport ? Number(linkedReport) : null,
      });
      setMovementOpen(false);
      setAmountValue('');
      notifications.show({
        color: signed > 0 ? 'green' : 'orange',
        title: `${target.invoiceNumber}: ${
          signed > 0 ? 'collection' : 'deduction'
        } recorded`,
        message: `${Math.abs(signed).toFixed(2)} ${target.dueCurrency}. Collected in total: ${(
          res.invoice.paidAmount ?? 0
        ).toFixed(2)} ${target.dueCurrency}.`,
      });
    } catch (error) {
      // A 409 carries what the invoice holds now. Their figures stay in the form and are
      // never applied to the new total on their behalf - they decide again, against the truth.
      const body = error instanceof ApiError ? safeJson(error.body) : null;
      if (body?.code === 'INVOICE_CHANGED') {
        setSnapshot({
          paidAmount: body.paidAmount,
          paymentSeq: body.paymentSeq,
          payments: body.payments,
        });
        setNotice({
          title: 'This invoice was updated while you were entering',
          message:
            'Someone else recorded against it. Check the history below, then confirm again if your entry is still right.',
          tone: 'orange',
        });
      } else if (
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500
      ) {
        setNotice({
          title: 'Not recorded',
          message: error.body || 'The entry was refused.',
          tone: 'red',
        });
      } else {
        // It may have landed. Saying it failed is how the same money gets entered twice.
        await refreshSnapshot();
        setNotice({
          title: "We didn't get a response",
          message:
            'Your entry may or may not have been recorded. Check the history below before entering it again.',
          tone: 'orange',
        });
      }
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const refreshSnapshot = async () => {
    if (!target) return;
    try {
      const fresh = await refetchInvoiceDetails(target.id.toString());
      setTarget(fresh);
      setSnapshot(snapshotOf(fresh));
    } catch {
      /* the notice already tells them what they do not know */
    }
  };

  // A recorded movement is never changed or deleted, so it goes in only after a 10 second undo.
  const countdown = useUndoCountdown(UNDO_SECONDS, () => void submitMovement());
  const locked = submitting || countdown.counting;
  const closeConfirm = () => {
    if (submitting) return;
    countdown.stop();
    setConfirmOpen(false);
  };

  const closeMovement = () => {
    if (submitting) return;
    countdown.stop();
    setMovementOpen(false);
  };

  const modals = (
    <>
      {target && (
        <PaymentConfirmationForm
          invoice={target}
          opened={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          onSubmit={async (data: PaymentConfirmationData) => {
            await addReport.mutateAsync({
              id: data.invoiceId,
              requestKey: reportKey,
              transactionHash: data.transactionHash,
              fileId: data.fileId,
              note: data.notes,
            });
            setReportKey(crypto.randomUUID());
            setPaymentOpen(false);
          }}
        />
      )}

      {target && (
        <Modal
          opened={movementOpen}
          onClose={closeMovement}
          title={`Record against ${target.invoiceNumber}`}
          size="md"
          centered
          closeOnClickOutside={!submitting}
          closeOnEscape={!submitting}
          withCloseButton={!submitting}
        >
          <Stack gap="md">
            {snapshot && (
              <DueSummary
                due={Number(target.dueAmount ?? 0)}
                collected={snapshot.paidAmount ?? 0}
                after={amountIsMoney ? resulting : null}
                currency={target.dueCurrency}
              />
            )}
            <SegmentedControl
              value={direction}
              onChange={(v) => setDirection(v as 'collection' | 'deduction')}
              disabled={locked}
              data={[
                { value: 'collection', label: 'Money received' },
                { value: 'deduction', label: 'Money returned' },
              ]}
              fullWidth
            />
            <NumberInput
              // The figure that moved now, not what the invoice holds - the confusion behind
              // most mis-entries, so the label says so outright.
              label={
                direction === 'collection'
                  ? 'Amount received'
                  : 'Amount returned'
              }
              description="What actually moved in the account, exactly as the statement reads it."
              placeholder="Enter amount"
              value={amountValue}
              onChange={setAmountValue}
              min={0}
              step={0.01}
              decimalScale={2}
              disabled={locked}
              required
            />
            <DateInput
              label="Actual date"
              description="The day the money moved, not today's entry"
              value={collectedDate}
              onChange={setCollectedDate}
              maxDate={new Date()}
              valueFormat="YYYY-MM-DD"
              disabled={locked}
              required
            />
            {direction === 'collection' && (
              <TextInput
                label="Payment came from"
                description="The sender as the statement or the chain shows it (optional)"
                value={senderName}
                onChange={(e) => setSenderName(e.currentTarget.value)}
                maxLength={255}
                disabled={locked}
              />
            )}
            {direction === 'collection' && activeReports.length > 0 && (
              <Select
                label="Settles payment confirmation"
                description="Link the client's report this money answers (optional)"
                placeholder="None"
                clearable
                data={activeReports.map((r) => ({
                  value: String(r.id),
                  label: `${r.reportedAt ? r.reportedAt.slice(0, 10) : 'Date unknown'} - ${
                    r.hash ? r.hash.slice(0, 18) : (r.fileName ?? 'proof file')
                  }`,
                }))}
                value={linkedReport}
                onChange={setLinkedReport}
                disabled={locked}
              />
            )}
            {snapshot && amountIsMoney && (
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  Collected so far: {snapshot.paidAmount.toFixed(2)}{' '}
                  {target.dueCurrency}
                </Text>
                <Text size="sm" c="dimmed">
                  After this entry: {resulting.toFixed(2)} {target.dueCurrency}
                </Text>
              </Stack>
            )}
            {notice && (
              <Alert color={notice.tone} title={notice.title}>
                {notice.message}
              </Alert>
            )}
            <PaymentHistory
              payments={snapshot?.payments}
              currency={target.dueCurrency}
            />
            <Group justify="flex-end">
              <Button
                variant="subtle"
                onClick={closeMovement}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={() => setConfirmOpen(true)}
                loading={submitting}
                disabled={
                  submitting ||
                  !snapshot ||
                  !amountIsMoney ||
                  !collectedDate ||
                  resulting < 0
                }
              >
                {resulting < 0
                  ? 'More than has been collected'
                  : `Record ${Math.abs(signed).toFixed(2)} ${target.dueCurrency}`}
              </Button>
            </Group>{' '}
          </Stack>
        </Modal>
      )}
      {target && (
        <Modal
          opened={confirmOpen}
          onClose={closeConfirm}
          title="Record this entry?"
          size="sm"
          centered
          closeOnClickOutside={!locked}
          closeOnEscape={!locked}
          withCloseButton={!locked}
        >
          <Stack gap="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {signed > 0 ? 'Received' : 'Returned'}
                </Text>
                <Text size="sm" fw={700} c={signed < 0 ? 'orange' : undefined}>
                  {signed > 0 ? '+' : '-'}
                  {Math.abs(signed).toFixed(2)} {target.dueCurrency}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Date
                </Text>
                <Text size="sm">
                  {collectedDate
                    ? collectedDate.split('-').reverse().join('.')
                    : '-'}
                </Text>
              </Group>
              {signed > 0 && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    From
                  </Text>
                  <Text size="sm">{senderName.trim() || 'not stated'}</Text>
                </Group>
              )}
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Collected after it
                </Text>
                <Text size="sm">
                  {resulting.toFixed(2)} {target.dueCurrency}
                </Text>
              </Group>
            </Stack>
            <Alert color="orange" p="xs">
              <Text size="sm">
                A recorded entry can never be changed or deleted - only offset
                by another entry.
              </Text>
            </Alert>
            {countdown.counting ? (
              <Stack gap={6}>
                <Progress
                  value={((countdown.remaining ?? 0) / UNDO_SECONDS) * 100}
                  size="sm"
                  color="orange"
                  transitionDuration={900}
                />
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm">Recording in {countdown.remaining} s</Text>
                  <Button color="red" variant="light" onClick={countdown.stop}>
                    Undo
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  onClick={closeConfirm}
                  disabled={submitting}
                >
                  Back
                </Button>
                <Button onClick={countdown.start} loading={submitting}>
                  Record
                </Button>
              </Group>
            )}
          </Stack>
        </Modal>
      )}

      {target && (
        <SettleIssueModal
          invoice={target}
          opened={settleOpen}
          onClose={() => setSettleOpen(false)}
        />
      )}

      {target && amendOpen && (
        <AmendInvoiceModal
          invoice={target}
          opened={amendOpen}
          onClose={() => setAmendOpen(false)}
        />
      )}

      {target && cancelOpen && (
        <CancelInvoiceModal
          invoice={target}
          ready={snapshot != null}
          loadFailed={snapshot == null && notice != null}
          opened={cancelOpen}
          onClose={() => setCancelOpen(false)}
        />
      )}
    </>
  );

  return { handlers, modals };
}

type ChangedBody = {
  code?: string;
  paidAmount: number;
  paymentSeq: number;
  payments: PaymentMovement[];
};

const safeJson = (body: string): ChangedBody | null => {
  try {
    return JSON.parse(body) as ChangedBody;
  } catch {
    return null;
  }
};
